import React, { useState, useEffect } from "react";
import AppIcon from "../../../components/ui/AppIcon";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useChat } from "../../../context/ChatContext";
import "./ThreadsListModal.css";

export default function ThreadsListModal({ onClose, onOpenThread }) {
  const { user } = useAuth();
  const { selectChat, channels, directMessages } = useChat();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchUserThreads();
  }, []);

  const handleRefresh = () => {
    setThreads([]);
    fetchUserThreads();
  };

  const handleNavigateToThread = (thread) => {
    const { chatId, chatType } = thread.parentMessage;

    // Get full channel/DM data from context
    if (chatType === "channel") {
      const channelData = channels.find(
        (c) => (c._id || c.id)?.toString() === chatId?.toString(),
      );
      if (channelData) {
        selectChat("channel", channelData);
      }
    } else {
      // For DM, find the DM from directMessages
      const dmData = directMessages.find(
        (d) => (d._id || d.id)?.toString() === chatId?.toString(),
      );
      if (dmData) {
        selectChat("dm", dmData);
      } else {
        // If not found in context, try to construct from chatId
        const userIds = chatId.split("_");
        const otherUserId = userIds.find((id) => id !== user?._id);
        if (otherUserId) {
          selectChat("dm", { _id: otherUserId, id: otherUserId, type: "dm" });
        }
      }
    }
    onClose();
  };

  // Ensure threads is always an array for rendering
  const safeThreads = Array.isArray(threads) ? threads : [];

  const fetchUserThreads = async () => {
    try {
      setLoading(true);
      const response = await api.get("/threads/user");

      const threadData = response?.data || response;

      if (Array.isArray(threadData)) {
        setThreads(threadData);
      } else {
        setThreads([]);
      }
    } catch (error) {
      console.error("Failed to fetch threads:", error);
      setThreads([]);
    } finally {
      setLoading(false);
    }
  };

  const threadList = Array.isArray(threads) ? threads : [];

  const filteredThreads = threadList.filter((thread) => {
    if (filter === "unread") return thread.hasUnread;
    if (filter === "my") return thread.isOwner || thread.isParticipant;
    return true;
  });

  return (
    <div className="threads-list-modal-overlay" onClick={onClose}>
      <div
        className="threads-list-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="threads-list-modal-header">
          <h2 className="threads-list-modal-title">
            <span className="threads-list-modal-title-icon">
              <AppIcon name="ChatBubbleBottomCenterIcon" size={18} />
            </span>
            Threads
          </h2>
          <div className="flex gap-2">
            <button
              className="threads-list-modal-close-btn"
              onClick={handleRefresh}
            >
              <AppIcon name="ArrowPathIcon" size={18} />
            </button>
            <button className="threads-list-modal-close-btn" onClick={onClose}>
              <AppIcon name="XMarkIcon" size={20} />
            </button>
          </div>
        </div>

        <div className="threads-list-modal-filters">
          <button
            className={`threads-filter-btn ${filter === "all" ? "active" : ""}`}
            onClick={() => setFilter("all")}
          >
            All
          </button>
          <button
            className={`threads-filter-btn ${filter === "unread" ? "active" : ""}`}
            onClick={() => setFilter("unread")}
          >
            Unread
          </button>
          <button
            className={`threads-filter-btn ${filter === "my" ? "active" : ""}`}
            onClick={() => setFilter("my")}
          >
            My Threads
          </button>
        </div>

        <div className="threads-list-modal-content">
          {loading ? (
            <div className="threads-list-loading">
              <AppIcon name="Spinner" className="animate-spin" />
              <span>Loading threads...</span>
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="threads-list-empty">
              <div className="threads-list-empty-icon">
                <AppIcon name="ChatBubbleBottomCenterIcon" size={28} />
              </div>
              <p>No threads yet</p>
              <span>
                Start a conversation and reply in threads to see them here
              </span>
            </div>
          ) : (
            <div className="threads-list">
              {filteredThreads.map((thread) => (
                <div
                  key={thread._id}
                  className="thread-list-item"
                  onClick={() => {
                    onOpenThread(
                      thread.parentMessage,
                      thread.parentMessage.chatId,
                      thread.parentMessage.chatType,
                    );
                    onClose();
                  }}
                >
                  <div className="thread-list-item-avatar">
                    <img
                      src={
                        thread.lastReply?.senderId?.profilePhoto ||
                        thread.lastReplyUser?.profilePhoto ||
                        `https://ui-avatars.com/api/?name=${encodeURIComponent(thread.lastReply?.senderId?.name || thread.lastReplyUser?.name || "U")}&background=random`
                      }
                      alt=""
                    />
                  </div>
                  <div className="thread-list-item-content">
                    <div className="thread-list-item-header">
                      <span className="thread-list-item-preview">
                        {thread.parentMessage?.content?.slice(0, 60) ||
                          thread.lastReply?.content?.slice(0, 60) ||
                          "Thread"}
                        ...
                      </span>
                      {thread.hasUnread && (
                        <span className="thread-unread-badge">
                          {thread.replyCount}
                        </span>
                      )}
                    </div>
                    <div className="thread-list-item-meta">
                      <span className="thread-list-item-channel">
                        <AppIcon
                          name={
                            thread.parentMessage?.chatType === "channel"
                              ? "HashtagIcon"
                              : "UserIcon"
                          }
                          size={12}
                        />
                        {thread.parentMessage?.chatName ||
                          thread.parentMessage?.chatId ||
                          (thread.parentMessage?.chatType === "channel"
                            ? "channel"
                            : "Direct Chat")}
                      </span>
                      <span className="thread-list-item-replies">
                        {thread.replyCount}{" "}
                        {thread.replyCount === 1 ? "reply" : "replies"}
                      </span>
                      <span className="thread-list-item-time">
                        {formatTimeAgo(
                          thread.lastReplyAt || thread.lastReply?.createdAt,
                        )}
                      </span>
                    </div>
                  </div>
                  <button
                    className="thread-navigate-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToThread(thread);
                    }}
                    title="Navigate to conversation"
                  >
                    <AppIcon name="ArrowRightIcon" size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimeAgo(date) {
  if (!date) return "";
  const now = new Date();
  const past = new Date(date);
  const diffMs = now - past;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return past.toLocaleDateString();
}
